import { useMemo } from 'react';
import { LEAVE_TYPES, LEAVE_TYPES_ALIASES } from 'domain/shared/leave';
import { canSelectMaternityLeaveType } from '../maternityLeaveEligibility';
import {
  isMinskOffice,
  isPolishOffice,
  isAustinOffice,
  isCFSContract,
  isContractorContract,
} from 'domain/shared/employee';
import { EnumDropdownProps, EnumDropdown } from 'framework/form';

const LeaveTypeDropdown = ({
  isOneStop = false,
  source = '',
  employee,
  ...props
}: {
  isOneStop?: boolean;
  employee?: any;
} & Omit<EnumDropdownProps, 'options'>) => {
  const options = useMemo(() => {
    if (!employee) {
      return LEAVE_TYPES;
    }

    const officeId = String(employee.officeId);
    const contractType = String(employee.contractType);
    const isContractor = isContractorContract(contractType);
    const isCFS = isCFSContract(contractType);
    const allowMaternity = canSelectMaternityLeaveType(officeId, contractType);

    let allowed: string[] = [];

    if (isMinskOffice(officeId)) {
      if (isContractor) {
        allowed = [LEAVE_TYPES_ALIASES.UNPAID_LEAVE];
      } else {
        allowed = [
          LEAVE_TYPES_ALIASES.PAID_LEAVE,
          LEAVE_TYPES_ALIASES.SICK_LEAVE,
          LEAVE_TYPES_ALIASES.SICK_DAY,
          LEAVE_TYPES_ALIASES.UNPAID_LEAVE,
          LEAVE_TYPES_ALIASES.MILITARY_LEAVE,
        ];
      }
      if (allowMaternity) {
        allowed.push(LEAVE_TYPES_ALIASES.MATERNITY_LEAVE);
      }
    } else if (isPolishOffice(officeId)) {
      if (isContractor) {
        allowed = [LEAVE_TYPES_ALIASES.UNPAID_LEAVE];
      } else {
        allowed = [
          LEAVE_TYPES_ALIASES.PAID_LEAVE,
          LEAVE_TYPES_ALIASES.SICK_LEAVE,
          LEAVE_TYPES_ALIASES.UNPAID_LEAVE,
          LEAVE_TYPES_ALIASES.LEAVE_FOR_PERSONAL_CIRCUMSTANCES,
        ];
        if (allowMaternity) {
          allowed.push(LEAVE_TYPES_ALIASES.MATERNITY_LEAVE);
        }
      }
    } else if (isAustinOffice(officeId)) {
      if (isCFS) {
        allowed = [LEAVE_TYPES_ALIASES.UNPAID_LEAVE];
      } else {
        allowed = [LEAVE_TYPES_ALIASES.PAID_LEAVE, LEAVE_TYPES_ALIASES.UNPAID_LEAVE, LEAVE_TYPES_ALIASES.SICK_DAY];
      }
    } else {
      allowed = Object.keys(LEAVE_TYPES);
    }

    return Object.fromEntries(Object.entries(LEAVE_TYPES).filter(([key]) => allowed.includes(key)));
  }, [employee]);

  const isLoading = !employee && isOneStop;
  return (
    <EnumDropdown
      source={source}
      options={!isLoading ? options : {}}
      label="Leave Type"
      placeholder={isLoading ? 'Loading…' : 'Choose Leave Type'}
      isLoading={isLoading}
      {...props}
    />
  );
};

export default LeaveTypeDropdown;
